import { AfterViewInit, Component, ElementRef, ErrorHandler, Input, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Business } from '../models/business.model';
import { QuickNavToc } from '../../shared/models/quick-nav-toc.model';
import { BusinessFuelComponent } from '../business-fuel/business-fuel.component';
import { BusinessAmenitiesComponent } from '../business-amenities/business-amenities.component';
import { BusinessSalesComponent } from '../business-sales/business-sales.component';
import { BusinessMaintenanceComponent } from '../business-maintenance/business-maintenance.component';
import { RentalsComponent } from '../rentals/rentals.component';
import { TrainingComponent } from '../training/training.component';
import { EducationalInfoComponent } from '../educational-info/educational-info.component';
import { FeesSectionComponent } from '../fees-section/fees-section.component';
import { RentalCarsComponent } from '../../shared/rental-cars/rental-cars.component';
import { QuickNavComponent } from '../../shared/quick-nav/quick-nav.component';
import { BusinessManagerService } from '../../shared/user/business-manager.service';
import { EditToggleService } from '../../shared/services/edit-toggle.service';
import { BusinessDescriptionComponent } from '../business-description/business-description.component';
import { AirportService } from '../../airport/airport.service';
import { getFteAwardTags, hasTag } from '../../util/tag-utils';
import { WhatsnewComponent } from '../../whatsnew/whatsnew.component';
import { MatDialog } from '@angular/material';
import { WhatsNewService } from '../../shared/services/whats-new.service';
import { Tag } from '../../shared/models/tag.model';
import { HelpDialogComponent } from '../help-dialog/help-dialog.component';
import { AnalyticsService } from '../../airport/service/google-analytics-4.service';
import { AuthService } from '../../auth/auth/auth.service';


@Component({
  selector: 'app-business-detail',
  templateUrl: './business-detail.component.html',
  styleUrls: ['./business-detail.component.scss']
})

export class BusinessDetailComponent implements OnInit, AfterViewInit {
  @Input() business: Business;
  isBusinessOwner = false;
  editModeSet = false;
  isFlightDeckAdmin = false;
  isRegionalManager = false;
  isFbo = false;
  isFlightSchool = false;
  displayHistoricalBadges = false;
  displayReportCard = false;
  awardTags: Tag[] = [];
  isDoneInit = false;
  isNotApproved = false;

  @ViewChild('quicknav') quicknav: QuickNavComponent;
  @ViewChild('body', { read: ElementRef }) body: ElementRef;
  @ViewChild(BusinessFuelComponent) businessFuelSection: BusinessFuelComponent;
  @ViewChild(BusinessAmenitiesComponent) servicesSection: BusinessAmenitiesComponent;
  @ViewChild(RentalCarsComponent) rentalCarsSection: RentalCarsComponent;
  @ViewChild(BusinessSalesComponent) salesSection: BusinessSalesComponent;
  @ViewChild(BusinessMaintenanceComponent) maintenanceSection: BusinessMaintenanceComponent;
  @ViewChild(FeesSectionComponent) feesSection: FeesSectionComponent;
  @ViewChild(RentalsComponent) rentalsSection: RentalsComponent;
  @ViewChild(EducationalInfoComponent) eduSection: EducationalInfoComponent;
  @ViewChild(TrainingComponent) trainingSection: TrainingComponent;
  @ViewChild(BusinessDescriptionComponent) businessDescriptionSection: BusinessDescriptionComponent;

  toc: QuickNavToc = new QuickNavToc('fbo-quicknav', [{
    label: 'Overview',
    fragment: 'overview',
    visible: true
  }, {
    label: 'Contact Info',
    fragment: 'contacts',
    visible: true
  }, {
    label: 'Business Description',
    fragment: 'desc',
    visible: () => this.businessDescriptionSection.hasContent
  }, {
    label: 'Fuel Prices',
    fragment: 'fuel',
    visible: () => this.businessFuelSection.hasContent
  }, {
    label: 'Fees',
    fragment: 'fees',
    visible: () => this.feesSection.hasContent
  }, {
    label: 'Services',
    fragment: 'services',
    visible: () => this.servicesSection.hasContent
  }, {
    label: 'Rental Agency',
    fragment: 'rental-agency',
    visible: () => this.rentalCarsSection.hasContent
  }, {
    label: 'Sales',
    fragment: 'sales',
    visible: () => this.salesSection.hasContent
  }, {
    label: 'Maintenance',
    fragment: 'maintenance',
    visible: () => this.maintenanceSection.hasContent
  }, {
    label: 'Rentals',
    fragment: 'rentals',
    visible: () => this.rentalsSection.hasContent
  }, {
    label: 'Educational Info',
    fragment: 'edu',
    visible: () => this.eduSection.hasContent
  }, {
    label: 'Flight Training',
    fragment: 'training',
    visible: () => this.trainingSection.hasContent
  }]);

  get faaIdent(): string {
    if (this.business.airports && this.business.airports.length > 0) {
      return this.business.airports[0].faaId;
    }
    return null;
  }

  constructor(private _route: ActivatedRoute,
              private _errorHandler: ErrorHandler,
              private businessManagerService: BusinessManagerService,
              private _toggleService: EditToggleService,
              private _airportService: AirportService,
              private _matDialog: MatDialog,
              private _whatsNewService: WhatsNewService,
              private _analyticsService: AnalyticsService,
              private _auth: AuthService) {
  }

  async ngOnInit() {
    this._route.data
      .subscribe((data: { business: Business }) => {
        this.business = data.business;
      }, (error: any) => {
        this._errorHandler.handleError(error);
      });
    this.isBusinessOwner = await this.businessManagerService.userOwnsBusiness(this.business.id);
    this.isFlightDeckAdmin = await this.businessManagerService.isFlightDeckAdmin();
    this._toggleService.toggleEmitter.subscribe(isOn => {
      this.editModeSet = isOn;
    });
    this.isRegionalManager = await this.businessManagerService.isRegionalManager()
      && !await this.businessManagerService.userOwnsBusiness(this.business.id);
    this.isFbo = hasTag(this.business.tags, 'Admin::BusinessIsFbo');
    this.isFlightSchool = (!this.business.tags.some(t => t.tagName.startsWith('Flight Training::Private'))
      && this.business.tags.some(t => t.tagName.startsWith('Flight Training')));
    this.displayHistoricalBadges = hasTag(this.business.tags, 'Admin::Flight Training::Display Prior Year Badges');
    this.displayReportCard = hasTag(this.business.tags, 'Admin::Flight Training::Display Customer Feedback Report');
    this.awardTags = getFteAwardTags(this.business.tags);
    this.isNotApproved = hasTag(this.business.tags, 'Admin::BusinessNotApproved');

    const showWhatsNew = await this._whatsNewService.shouldShowWhatsNew();
    if (showWhatsNew) {
      const dialogRef = this._matDialog.open(WhatsnewComponent);
      dialogRef.afterClosed().subscribe(result => {
        this._whatsNewService.displayedWhatsNew();
      });
    }
    this.isDoneInit = true;
    this._analyticsService.pageView('airport_business', 'Business Detail');
    this._analyticsService.trackEvent('airport_business_viewed', this.business.id.toString(), this.business.id.toString());
    if (this._auth.authenticated && this._auth.endUser) {
      this._analyticsService.trackEvent('airport_business_loggedin', this._auth.recid, this._auth.recid);
    } else {
      this._analyticsService.trackEvent('airport_business_loggedout', '', '');
    }
  }

  async onReloadBusiness() {
    this.business = await this._airportService.getBusiness(this.business.id.toString());
    this.displayHistoricalBadges = hasTag(this.business.tags, 'Admin::Flight Training::Display Prior Year Badges');
    this.awardTags = getFteAwardTags(this.business.tags);
    this.displayReportCard = hasTag(this.business.tags, 'Admin::Flight Training::Display Customer Feedback Report');
    this.isNotApproved = hasTag(this.business.tags, 'Admin::BusinessNotApproved');
  }

  ngAfterViewInit() {
    /*
    const $detailBody = $(this.body.nativeElement);
    const $page = $detailBody.closest('.detail-page');
    this.quicknav.setContainerHeight($page.height() + 1000);
    */
    if (hasTag(this.business.tags, 'Admin::BusinessNotApproved')) {
      this.showHelp();
    }
  }

  showHelp() {
    let businessType = 'Business';
    if (hasTag(this.business.tags, 'Admin::BusinessIsFbo')) {
      businessType = 'FBO';
    } else if ((!this.business.tags.some(t => t.tagName.startsWith('Flight Training::Private'))
      && this.business.tags.some(t => t.tagName.startsWith('Flight Training')))) {
      businessType = 'Flight School';
    }
    const dialogRef = this._matDialog.open(HelpDialogComponent, {
      data: {
        businessType: businessType
      }
    });
  }
}
